// What the shell follows after an operator approves a site's work list (#1811):
// each entry's own outcome, as the approval answered it, and then the site's
// convergence for the devices those entries reached, until every one of them
// reads `synchronized`, `behind` or `unknown`.
//
// **There is no timer and no bound.** Nothing here decides how long to follow
// or how often to ask: the shell reads the convergence again when the operator
// moves between pages and after the operator's own write, the same triggers as
// its work list, and stops reading once every device has settled. A device
// that never settles — offline, or still syncing — is followed until the
// operator stops following it or the tab closes. A fixed window would be a
// value nobody has measured (ZENITH.md §29), and none is chosen here.
//
// **Settled is not synchronized.** `behind` and `unknown` end the following
// because nothing more will happen to them without an act, and each is shown
// as its own word, never as current. `synchronizing`, `offline` and `carried`
// keep it going.
//
// **And every apply's own record** (ADR-0480 decision 4a, #2064), below: what
// the change stream says moved, read wherever the operator is.
import { useSyncExternalStore } from "react";
import { api, unattended } from "../api/client";
import { describe } from "../api/problem";
import type { components } from "../api/schema";
import { unrecognized } from "../api/openEnum";

type Outcome = components["schemas"]["WorkListOutcome"];
type SiteConvergence = components["schemas"]["SiteConvergence"];
// The named lists: since ADR-0511 each response field is its list or any other string.
type ConvergenceState = components["schemas"]["DeviceConvergenceState"];

/** One approval being followed: its entries in the order sent, each with the
 * name the work list gave its target. */
export interface Follow {
  siteId: string;
  entries: { outcome: Outcome; name: string }[];
}

const follows = new Map<string, Follow>();
const listeners = new Set<() => void>();
const changed = () => { for (const l of listeners) l(); };

/** Starts following what an approval sent at a site, replacing anything the
 * site was being followed for. */
export function follow(siteId: string, outcomes: Outcome[], name: (target: string) => string): void {
  follows.set(siteId, { siteId, entries: outcomes.map((outcome) => ({ outcome, name: name(outcome.target) })) });
  changed();
}

/** Stops following a site. */
export function stopFollowing(siteId: string): void {
  if (follows.delete(siteId)) changed();
}

/** Forgets every approval being followed, for a test that must not see the
 * one the test before it approved. */
export function forgetFollows(): void {
  follows.clear();
  forgetApplies();
}

/** What the site is being followed for, or undefined. */
export function useFollow(siteId: string | undefined): Follow | undefined {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => siteId ? follows.get(siteId) : undefined,
  );
}

/** The states that end the following for a device. */
export const SETTLED: ReadonlySet<string> = new Set<ConvergenceState>(["synchronized", "behind", "unknown"]);

/** The devices an approval reached: every access point the convergence read
 * names for a venue entry the controller took, and the switch itself for a
 * switch entry it took or that did not answer in time. A refused entry
 * reached nothing. */
export function followed(f: Follow, data: SiteConvergence): string[] {
  const out = new Set<string>();
  for (const { outcome: o } of f.entries) {
    if (o.outcome === "refused") continue;
    if (o.target === "venue") {
      for (const d of data.devices) if (d.category === "ap") out.add(d.serial);
    } else if (o.target.startsWith("switch:")) {
      out.add(o.target.slice("switch:".length));
    }
  }
  return [...out];
}

/** Whether every device an approval reached has settled, from one
 * convergence read. A device the read does not name has not settled, and a
 * read that could not judge the site (its placement) settles nothing. */
export function settledOf(f: Follow, data: SiteConvergence): { serials: string[]; settled: number; done: boolean } {
  const serials = followed(f, data);
  const state = new Map(data.devices.map((d) => [d.serial, d.state]));
  const settled = serials.filter((s) => { const st = state.get(s); return st !== undefined && SETTLED.has(st); }).length;
  const done = data.reason !== "placement" && settled === serials.length;
  return { serials, settled, done };
}

/** Whether an approval reached any device at all: one whose every entry was
 * refused has nothing to follow. */
export function reachedAny(f: Follow): boolean {
  return f.entries.some((e) => e.outcome.outcome !== "refused");
}

// ---------------------------------------------------------------------------
// Every apply's own record (ADR-0480 decision 4a, #2064).
//
// An apply route answers `202` with an id once Zenith's own checks pass, and
// its outcome is the apply's record at `GET /v1/applies/{id}` (decision 3a).
// The page the operator applied from hands that id here and is free to be
// left; the shell (`AppliesFollowed`) reads each record **when the change
// stream says an apply's record moved**, and again when the operator moves
// between pages or makes a write, the triggers above. **No timer and no
// polling interval** (§29): a record whose process stopped reads
// `interrupted` (ADR-0536 decision 1a), which ends the following and says
// *stopped*, never *applied*; one still `running` is followed until it ends,
// the operator stops following it or the tab closes.
//
// **The id is followed from whatever route answered with it.** A route still
// answering as it did before ADR-0480 hands nothing here (`acceptedApplyOf`
// reads only `ApplyAccepted`'s shape), and its page renders that answer
// exactly as it always has; each route moves by its family's item (#2059 to
// #2063).
//
// **A `404` ends the following.** The record is answered under its own
// route's scope and anybody else is told `404` (§6.3): not yours, or gone. It
// is never read again, and it is shown as no longer readable, not as an error.
// Any other failed read is kept and read again on the next trigger, which is
// an event and never a retry loop.
type Apply = components["schemas"]["Apply"];
type ApplyState = components["schemas"]["ApplyState"];

/** An apply being followed: the record's id, what the page that sent it
 * called it, and the last thing its read answered. */
export interface FollowedApply {
  id: string;
  what: string;
  /** The record as last read; absent until the first read answers. */
  record?: Apply;
  /** The read answered 404: not this caller's to read, or gone. Never read again. */
  gone?: boolean;
  /** The last read failed some other way; read again on the next trigger. */
  problem?: string;
}

/** The states an apply's record ends in (migration 0141), and `interrupted`,
 * which is read and never stored: the record was open and the process running
 * it is gone (ADR-0536 decision 1a). Nothing more will happen to it, so it is
 * not read again. `accepted` and `running` are open. */
export const APPLY_ENDED: ReadonlySet<string> = new Set<ApplyState>(["done", "refused", "failed", "unknown", "interrupted"]);

const applies = new Map<string, FollowedApply>();
let appliesSnapshot: FollowedApply[] = [];
const appliesChanged = () => {
  appliesSnapshot = [...applies.values()];
  changed();
};

/** The id an apply route's answer carries when it is `ApplyAccepted` — a
 * `202` whose body is the record's id and nothing else — or undefined for a
 * route that still answers with its own body. */
export function acceptedApplyOf(status: number, data: unknown): string | undefined {
  if (status !== 202 || typeof data !== "object" || data === null) return undefined;
  const keys = Object.keys(data);
  const id = (data as { id?: unknown }).id;
  return keys.length === 1 && typeof id === "string" && id !== "" ? id : undefined;
}

/** Starts following an apply's record. Following one already followed
 * changes nothing: a replay answers the same id (ADR-0480 decision 7a). */
export function followApply(id: string, what: string): void {
  if (applies.has(id)) return;
  applies.set(id, { id, what });
  appliesChanged();
  // Handed over is the first trigger: the record is read once now, whichever page holds it.
  void readApplies(readApply);
}

/** Hands an apply route's answer to the shell when it is `ApplyAccepted`, and
 * says whether it did. A page calls it first with every apply's answer, and
 * renders the answer as it always has where this says no. It answers the
 * apply's id where it handed one over, and nothing otherwise. */
export function handOff(r: { ok: boolean; status: number; data?: unknown }, what: string): string | undefined {
  const id = r.ok ? acceptedApplyOf(r.status, r.data) : undefined;
  if (!id) return undefined;
  followApply(id, what);
  return id;
}

/** Stops following an apply. Its record is untouched. The id is remembered
 * as dismissed in this browser, so a new tab does not find it again
 * (`findApplies`). */
export function stopFollowingApply(id: string): void {
  if (!applies.delete(id)) return;
  const kept = dismissed();
  kept.add(id);
  keepDismissed(kept);
  appliesChanged();
}

// A new tab finds the caller's applies again (ADR-0536 decisions 3a and 4a,
// #2223). **Once, on the tab's first draw**, the shell reads
// `GET /v1/applies?open=true`: the caller's own records that have not ended as
// stored and that `getApply` answers them, each `accepted`, `running` or
// `interrupted`. It follows each one this browser has not dismissed: a running
// one until it ends, on the same triggers as one a page handed over, and an
// interrupted one until the operator dismisses it. No timer (§29).
//
// **A dismissal is kept in this browser** (`localStorage`), and pruned to the
// ids the list still returns, so it never grows past the list. Another browser
// shows the same interrupted apply again; nothing is written to the server.

const DISMISSED = "zenith.applies.dismissed";

function dismissed(): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(DISMISSED);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((i): i is string => typeof i === "string") : []);
  } catch {
    return new Set();
  }
}

function keepDismissed(ids: Set<string>): void {
  try { globalThis.localStorage?.setItem(DISMISSED, JSON.stringify([...ids])); } catch { /* not kept: shown again */ }
}

/** What an apply found again is called: its route, in an operator's words,
 * and its target. A page that hands one over names it itself. */
export function whatOf(rec: Apply): string {
  const words: Record<string, string> = {
    site_config: "Site apply", device_config: "Access point apply", site_reconcile: "Reconciliation",
    carried_apply: "Carried apply", carried_seed: "Carry-over seed", work_list: "Changes to apply",
    vlan_references: "Tag removal", switch_ports: "Ports apply", switch_first_document: "First configuration",
    switch_cli_settings: "Switch setting", site_gateway: "Gateway apply",
  };
  const word = words[rec.kind] ?? "Apply";
  return rec.target.kind === "switch" || rec.target.kind === "access_point" ? `${word} on ${rec.target.id}` : word;
}

/** Follows the records `list` returned that this browser has not dismissed and
 * that are not followed already, and forgets dismissals the list no longer
 * returns. */
export function adoptApplies(list: Apply[]): void {
  const kept = dismissed();
  const listed = new Set(list.map((r) => r.id));
  let added = false;
  for (const rec of list) {
    if (applies.has(rec.id) || kept.has(rec.id)) continue;
    applies.set(rec.id, { id: rec.id, what: whatOf(rec), record: rec });
    added = true;
  }
  const pruned = new Set([...kept].filter((id) => listed.has(id)));
  if (pruned.size !== kept.size) keepDismissed(pruned);
  if (added) appliesChanged();
}

let found = false;

/** Reads the caller's open applies once per tab and follows them. A failed read
 * finds nothing and is not retried: the tab's own applies are followed as they
 * are handed over. */
export async function findApplies(): Promise<void> {
  if (found) return;
  found = true;
  try {
    const r = await api.GET("/applies", { params: { query: { open: true }, header: unattended({ unattended: true }) } });
    if (r.data) adoptApplies(r.data.applies);
  } catch { /* nothing found; nothing retried */ }
}

/** Every apply followed, in the order followed. */
export function useFollowedApplies(): FollowedApply[] {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => appliesSnapshot,
  );
}

/** The followed apply with this id, as the shell last read it. */
export function useFollowedApply(id: string | undefined): FollowedApply | undefined {
  const all = useFollowedApplies();
  return id ? all.find((a) => a.id === id) : undefined;
}

/** Whether an apply is still to be read: not gone, and not ended. */
export function applyOpen(a: FollowedApply): boolean {
  return !a.gone && !(a.record && APPLY_ENDED.has(a.record.state));
}

/** What one read of a record answered: the record, a 404, or another failure. */
export type ApplyRead =
  | { kind: "record"; record: Apply }
  | { kind: "gone" }
  | { kind: "failed"; message: string };

/** Reads every apply still open, once each, through `read`, and keeps what
 * each answered. An ended or gone apply is not read again. */
export async function readApplies(read: (id: string) => Promise<ApplyRead>): Promise<void> {
  const open = [...applies.values()].filter(applyOpen);
  await Promise.all(open.map(async (a) => {
    const r = await read(a.id);
    if (!applies.has(a.id)) return;
    const next: FollowedApply = { id: a.id, what: a.what, record: a.record };
    if (r.kind === "record") next.record = r.record;
    else if (r.kind === "gone") next.gone = true;
    else next.problem = r.message;
    applies.set(a.id, next);
  }));
  if (open.length > 0) appliesChanged();
}

/** One read of one record: the record, a 404, or the failure in words. */
export async function readApply(id: string): Promise<ApplyRead> {
  try {
    const r = await api.GET("/applies/{apply_id}", { params: { path: { apply_id: id }, header: unattended({ unattended: true }) } });
    if (r.data) return { kind: "record", record: r.data };
    if (r.response.status === 404) return { kind: "gone" };
    const p = describe(r.error, r.response.status);
    return { kind: "failed", message: p.message ?? p.reason };
  } catch (e) {
    const p = describe(e);
    return { kind: "failed", message: p.message ?? p.reason };
  }
}

/** Forgets every apply followed, for a test (`forgetFollows` calls it). */
export function forgetApplies(opts: { keepDismissed?: boolean } = {}): void {
  applies.clear();
  appliesSnapshot = [];
  found = false;
  if (opts.keepDismissed) return;
  try { globalThis.localStorage?.removeItem(DISMISSED); } catch { /* none kept */ }
}

type Tone = "good" | "warn" | "serious" | "info" | "none";

/** The record's state in an operator's words. */
export function wordsOf(a: FollowedApply): { word: string; tone: Tone; detail?: string } {
  if (a.gone) return { word: "no longer readable", tone: "none", detail: "Its record is not one you can read now, or it is gone." };
  const rec = a.record;
  const said = (rec?.result as { message?: unknown } | undefined)?.message;
  const message = typeof said === "string" && said !== "" ? said : undefined;
  switch (rec?.state) {
    case undefined:
    case "accepted":
      return { word: "accepted", tone: "info", detail: "Zenith accepted it; nothing has been sent yet." };
    case "running":
      return { word: "running", tone: "info" };
    case "done":
      return { word: "finished", tone: "good" };
    case "refused":
      return { word: "refused", tone: "warn", detail: message ?? rec.reason };
    case "failed":
      return { word: "failed", tone: "serious", detail: message ?? rec.reason };
    // Restates ADR-0480 decision 6a, as ADR-0552 amends it — a controller that did not answer inside the
    // work's wait leaves the outcome not known, and nothing resends it (5a). The record's message says
    // which service did not answer and after how long (#2337); without one, the fixed sentence stands.
    case "unknown":
      return { word: "outcome not known", tone: "none",
        detail: message !== undefined
          ? `No answer came back: ${message}. Whether it was acted on is not known. Nothing was sent again.`
          : "An answer did not come back in time, so whether it was acted on is not known. Nothing was sent again." };
    // Restates ADR-0536 decision 1a and ADR-0480 decision 5a — the process running it is gone, read
    // from its lock; nothing resumed or retried it, so what each device runs is its own page's.
    case "interrupted":
      return { word: "stopped", tone: "warn",
        detail: "Stopped before it finished: a restart ended the process running it. Nothing was resumed or sent again. Each device's page says what it runs; apply again if it is still needed." };
    // Restates ADR-0511 decision 5a — a state this build does not know is shown as received, marked,
    // and is not read as ended.
    default:
      return { word: unrecognized(rec?.state), tone: "warn", detail: "This page does not recognize this state. Reload the page: a newer version may know it." };
  }
}
